import { Formik } from 'formik'
import * as Yup from 'yup'

import { type StopAppointment } from 'core/appointments/domain/Appointment'

import { getAppointmentDate, getInitialValuesFromAppointment } from '../../domain/Appointment'
import { type AppointmentFormValues } from '../../infra/ShipmentRepository'
import { AppointmentDialogForm } from './AppointmentDialogForm'

const initialValues: AppointmentFormValues = {
  mode: 'appt',
  type: 'requested',
  loadingType: 'live',
  notifyShipper: false,
  status: 'pending',
  misschedulingReason: {
    label: '',
    value: '',
  },
  misschedulingNote: '',
  timeType: {
    label: 'Specific time',
    value: 'at',
  },
  date: '',
  time: '',
  timeEnd: '',
}

const timeValidation = Yup.string().test(
  'militaryFormat',
  'Time must have 4 numbers (military time format)',
  function (value, context) {
    const valueString = `${value}`
    const timeTypeRef = this.resolve(Yup.ref('timeType.value'))
    const field = context.path

    if (field === 'timeEnd' && timeTypeRef === 'at') {
      return true
    }

    return valueString.length === 4
  }
)

const defaultValidationSchema = {
  mode: Yup.string().required(),
  type: Yup.string().required(),
  loadingType: Yup.string().required(),
  notifyShipper: Yup.boolean().nullable(),
  date: Yup.string().required(),
  time: timeValidation.required(),
  timeType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .typeError('Please specify a time window'),
  timeEnd: timeValidation
    .nullable()
    .test('validateTime', 'Time start must be less than time end', function (value) {
      const timeStart: number = this.resolve(Yup.ref('time'))
      const timeEnd = value ?? 0
      const timeTypeRef = this.resolve(Yup.ref('timeType.value'))

      if (timeTypeRef === 'at') return true

      return Number(timeStart) <= Number(timeEnd)
    }),
}

const scheduledValidationSchema = {
  ...defaultValidationSchema,
  misschedulingReason: Yup.object().when('status', {
    is: 'confirmed',
    then: () =>
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
    otherwise: () =>
      Yup.object().shape({
        label: Yup.string().nullable(),
        value: Yup.string().nullable(),
      }),
  }),
  misschedulingNote: Yup.string().when('misschedulingReason.value', {
    is: 'other',
    then: () => Yup.string().required('Must provide a note'),
  }),
}

type AppointmentDialogProps = {
  shipmentUUID: string
  stopIndex: number
  stopDate: string | null
  appointment?: StopAppointment
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

export function AppointmentDialog({
  shipmentUUID,
  stopIndex,
  stopDate,
  appointment,
  isOpen,
  onClose,
  onConfirm,
}: AppointmentDialogProps) {
  if (!isOpen) return null

  const formInitialValues = appointment
    ? getInitialValuesFromAppointment(initialValues, appointment)
    : initialValues

  const validationSchema = appointment
    ? Yup.object(scheduledValidationSchema)
    : Yup.object(defaultValidationSchema)

  const appointmentDate = getAppointmentDate({ appointment, stopDate })

  return (
    <Formik<AppointmentFormValues>
      initialValues={{
        ...formInitialValues,
        date: appointmentDate,
      }}
      initialTouched={{
        time: true,
      }}
      validationSchema={validationSchema}
      onSubmit={() => undefined}
      enableReinitialize
      validateOnMount
    >
      {() => {
        return (
          <AppointmentDialogForm
            shipmentUUID={shipmentUUID}
            stopIndex={stopIndex}
            appointment={appointment}
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        )
      }}
    </Formik>
  )
}
